import type { UserRole } from './constants';

/**
 * Permission matrix. The API enforces these server-side on every route; the web app only uses them to hide
 * controls a user cannot use. Row-level lead visibility is additionally governed by the `access` setting
 * (reps see all leads, or only their own + unassigned).
 */
export const PERMISSIONS = [
  'lead:read',
  'lead:write',
  'lead:delete',
  'lead:assign',
  'lead:export',
  'lead:import',
  'lead:merge',
  'lead:restore',
  'email:send',
  'email:bulk',
  'email:approve',
  'discovery:run',
  'templates:manage',
  'automation:manage',
  'suppression:manage',
  'settings:manage',
  'scoring:manage',
  'users:manage',
  'audit:read',
  'ai:use',
  /** See every Notes ticket, reply as the administrators and set its status (everyone can send and follow their own). */
  'ticket:manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const REP: Permission[] = [
  'lead:read',
  'lead:write',
  'lead:export',
  'lead:import',
  'email:send',
  'email:bulk',
  'discovery:run',
  'ai:use',
];

const MANAGER: Permission[] = [
  ...REP,
  'lead:delete',
  'lead:assign',
  'lead:merge',
  'lead:restore',
  'email:approve',
  'templates:manage',
  'automation:manage',
  'suppression:manage',
  'scoring:manage',
  'audit:read',
];
type PermissionFlags = Readonly<Partial<Record<Permission, true>>>;
const flags = (list: readonly Permission[]): PermissionFlags =>
  Object.fromEntries(list.map((p) => [p, true])) as PermissionFlags;

export const ROLE_PERMISSIONS: Record<UserRole, PermissionFlags> = {
  admin: flags(PERMISSIONS),
  manager: flags(MANAGER),
  sales_rep: flags(REP),
  viewer: flags(['lead:read']),
};

export function hasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role][permission] === true;
}

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  manager: 'Sales manager',
  sales_rep: 'Sales rep',
  viewer: 'Viewer (read-only)',
};
